import React, { createContext, useContext, useEffect } from 'react';
import { isBrowser, getWindow } from '../utils/ssrUtils';

interface PlausibleContextType {
  trackEvent: (eventName: string, props?: { props?: Record<string, string | number> }) => void;
}

const PlausibleContext = createContext<PlausibleContextType>({
  trackEvent: () => {},
});

interface PlausibleProviderProps {
  children: React.ReactNode;
  domain: string;
  trackOutboundLinks?: boolean;
  trackFileDownloads?: boolean;
  apiHost?: string;
}

const PlausibleProvider: React.FC<PlausibleProviderProps> = ({ 
  children, 
  domain,
  trackOutboundLinks = true,
  trackFileDownloads = true,
  apiHost = 'https://plausible.io'
}) => {
  useEffect(() => {
    if (!isBrowser()) return;

    // Set up proxy to queue events before script is loaded
    const window = getWindow();
    if (window) {
      window.plausible = window.plausible || function() { 
        ((window.plausible as any).q = (window.plausible as any).q || []).push(arguments);
      };
    }

    const scriptId = 'plausible-script';
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.defer = true;
    script.setAttribute('data-domain', domain);
    
    if (trackOutboundLinks) {
      script.setAttribute('data-outbound-links', 'true');
    }
    
    if (trackFileDownloads) {
      script.setAttribute('data-file-downloads', 'true');
    }
    
    script.src = `${apiHost}/js/script.js`;
    
    // Remove any old script before adding the new one to ensure props are updated
    document.getElementById(scriptId)?.remove();
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      document.getElementById(scriptId)?.remove();
    };
  }, [domain, trackOutboundLinks, trackFileDownloads, apiHost]);

  // Function to track custom events
  const trackEvent = (eventName: string, props?: { props?: Record<string, string | number> }) => {
    if (!isBrowser()) return;
    
    const window = getWindow();
    if (window && window.plausible) {
      window.plausible(eventName, props);
    }
  };

  return (
    <PlausibleContext.Provider value={{ trackEvent }}>
      {children}
    </PlausibleContext.Provider>
  );
};

// Custom hook to use Plausible
export const usePlausible = () => {
  const { trackEvent } = useContext(PlausibleContext);
  return trackEvent;
};


export default PlausibleProvider; 